// Step 7: pivot or persevere. Three questions answered from the graph:
// did customers confirm the problem (needs), are you building what they asked
// for (capabilities, in-idea × asked-for), which assumptions the evidence confirmed or refuted
// (assumptions with AI-proposed, PM-confirmed verdicts). The re-ground pre-step
// sits above them unchanged. This file is the shell; each question is its own
// section component.

import { Loader2, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProject, useUpdateProject } from '@/hooks/use-projects';
import { useAlignment, useAlignmentProposal, useGenerateAlignment } from '@/hooks/use-alignment';
import { useUIStore } from '@/stores';
import { WORKSPACE_WIDTH } from '@/lib/workspace-width';
import { toast } from '@/hooks/use-toast';
import { ReattachSection } from '@/features/validation/ReattachSection';
import { StepHeader } from '@/features/project/StepHeader';
import { PrerequisiteHint } from '@/features/project/PrerequisiteHint';
import { useMissingPrerequisite } from '@/features/project/use-missing-prerequisite';
import { NeedsSection } from '@/features/validation/NeedsSection';
import { ReviewBar } from '@/features/validation/ReviewBar';
import { MappingSection } from '@/features/validation/MappingSection';
import { VerdictsSection } from '@/features/validation/VerdictsSection';

interface ThesisAlignmentViewProps {
  projectId: string;
}

export function ThesisAlignmentView({ projectId }: ThesisAlignmentViewProps) {
  const { data: project } = useProject(projectId);
  const { data: report, isLoading, isError } = useAlignment(projectId);
  const updateProject = useUpdateProject();
  const { data: row, isLoading: rowLoading, isError: rowError } = useAlignmentProposal(projectId);
  const generate = useGenerateAlignment(projectId);
  const ideaNodeId = project?.ideaNodeId;

  const onGenerate = async () => {
    try {
      await generate.mutateAsync();
    } catch (err) {
      toast({ title: "Couldn't weigh the evidence", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  // A step id when there is nothing for the report to reason about (no idea, or
  // no assumptions, synthesised needs or capabilities), null when usable,
  // undefined while the graph is still loading.
  const missing = useMissingPrerequisite(projectId, 'thesis');

  // Viewers get everything read-only; every write below is gated on this.
  const canEdit = useUIStore((s) => s.canEdit);

  const reviewed = !!project?.thesisReviewed;
  const setReviewed = async (value: boolean) => {
    try {
      await updateProject.mutateAsync({ projectId, patch: { thesisReviewed: value } });
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-muted/30">
      <div className={`container ${WORKSPACE_WIDTH} py-8 space-y-6`}>
        <StepHeader
          projectId={projectId}
          stepId="thesis"
          actions={
            <div className="flex items-center gap-2 shrink-0">
              {reviewed ? (
                <Button variant="outline" onClick={() => setReviewed(false)} disabled={updateProject.isPending}>
                  Re-open step
                </Button>
              ) : report ? (
                // Only once the report has loaded: "complete" means the PM has
                // seen the three answers.
                <Button variant="outline" onClick={() => setReviewed(true)} disabled={updateProject.isPending}>
                  <Check className="h-4 w-4 mr-2" />
                  Mark step complete
                </Button>
              ) : null}
            </div>
          }
        />

        {missing ? (
          <PrerequisiteHint stepId="thesis" missing={missing} />
        ) : (
          <>
            <ReattachSection projectId={projectId} canEdit={canEdit} />

            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : isError || !report ? (
              <div className="rounded-lg border bg-card p-5 flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Couldn't load the review. Try reopening this step.
              </div>
            ) : (
              <>
                <NeedsSection needs={report.needs} />
                <ReviewBar row={row} isLoading={rowLoading} isError={rowError} canEdit={canEdit} generating={generate.isPending} onGenerate={onGenerate} />
                <MappingSection projectId={projectId} ideaNodeId={ideaNodeId} report={report} row={row} canEdit={canEdit} />
                <VerdictsSection projectId={projectId} report={report} row={row} canEdit={canEdit} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
